import React from 'react';
import type { DropdownProps } from 'wix-ui-tpa/cssVars';
import {
  Tabs,
  TabsSkin,
  Dropdown,
  DropdownTheme,
  OverflowBehavior,
  Divider,
  TabsVariant,
  TabsAlignment,
} from 'wix-ui-tpa/cssVars';
import { useNavigationWithSectionsLogic } from './NavigationWithSections.logic';
import type { PopulatedMenu } from 'root/apiTypes';
import type { NavigateToFn } from 'root/clientTypes';
import { NavigationDropdownStyle } from 'root/consts';
import { classes } from './NavigationWithSections.st.css';
import { dataHooks } from 'root/dataHooks';
import type { ILocation } from '@wix/yoshi-flow-editor';

export const NavigationWithSections = ({
  activeMenuIndex,
  setActiveItem,
  menus,
  navigateTo,
  currentUrl,
}: {
  activeMenuIndex: number;
  setActiveItem: (index: number) => void;
  menus: PopulatedMenu[];
  navigateTo: NavigateToFn;
  currentUrl: ILocation['url'];
}) => {
  const {
    isRTL,
    dropdownOptions,
    onClickNavigationDropdownOption,
    navigationDropdownStyle,
    dropdowmPlacement,
    dropdownFlip,
    t,
    shouldShowMenusDropdown,
    activeSection,
    showSectionNavigation,
    navigationContainerRef,
    handleTabClick,
    isStretchFullWidth,
  } = useNavigationWithSectionsLogic({
    menus,
    setActiveItem,
    navigateTo,
    currentUrl,
    activeMenuIndex,
  });

  return (
    <div
      ref={navigationContainerRef}
      className={`${classes.navigationContainer} ${shouldShowMenusDropdown ? '' : classes.withoutMenusDropdown}`}
      data-hook={dataHooks.sectionNavigation.container}
      role={t('component.menu.navigation.role')}
    >
      {shouldShowMenusDropdown && (
        <div className={`${classes.menusDropdownContainer}`}>
          <Dropdown
            aria-label={t('component.menu.multi-menu.aria-label')}
            className={classes.dropdown}
            data-hook={dataHooks.navigation.dropdown}
            flip={dropdownFlip}
            initialSelectedId={activeMenuIndex.toString()}
            options={dropdownOptions}
            placement={dropdowmPlacement as DropdownProps['placement']}
            theme={navigationDropdownStyle === NavigationDropdownStyle.Box ? DropdownTheme.Box : DropdownTheme.Line}
            onChange={onClickNavigationDropdownOption}
          />
        </div>
      )}
      {showSectionNavigation && (
        <>
          {shouldShowMenusDropdown && <Divider className={classes.navigationDivider} direction="vertical" />}
          <Tabs
            activeTabIndex={activeSection}
            alignment={isRTL ? TabsAlignment.right : TabsAlignment.left}
            aria-label={t('component.menu.multi-section.aria-label')}
            className={classes.sectionTabs}
            data-hook={dataHooks.sectionNavigation.tabs}
            overflowBehavior={OverflowBehavior.scroll}
            skin={TabsSkin.clear}
            variant={isStretchFullWidth ? TabsVariant.fullWidth : TabsVariant.fit}
            items={menus[activeMenuIndex].sections.map((section) => {
              return {
                title: section.name,
              };
            })}
            onTabClick={(index) => {
              handleTabClick(index);
            }}
          />
        </>
      )}
    </div>
  );
};
